import {
  CreateTask,
  HabitSchedule,
  ID,
  initialDateFormat,
  LimitKeys,
  TaskPriorityOptions,
  Timestamp,
} from '@griply/types';
import {
  getDateFormat,
  getTimeFormat,
  getWeekStartsOn,
  isNumber,
} from '@griply/utils';
import { useSortedGoals } from 'features/goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import { useSortedTags } from 'features/tags';
import { useCreateHabitAllowed, useUpdateTask } from 'features/tasks';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { NewTaskCard } from 'shared/components/ui/task-card';
import { useCreateMultipleTasksDialog } from 'shared/contexts/create-multiple-tasks-dialog';
import { useOpenPremiumDialog } from 'shared/contexts/premium-dialog';
import { useOpenCreateTask } from 'shared/contexts/task-detail';
import { useClickOutside } from 'shared/hooks/use-click-outside';
import { useCreateTaskWithEntities } from 'shared/hooks/use-create-task-with-entities';
import { useCreateTasksWithEntities } from 'shared/hooks/use-create-tasks-with-entities';
import { useForwardedRef } from 'shared/hooks/use-forwarded-ref';
import { useNewTaskState } from 'shared/hooks/use-new-task-state';
import { usePremiumFeatureAllowed } from 'shared/hooks/use-premium-feature-allowed';
import { useUser } from 'shared/hooks/use-user';
import { useI18nLocale } from 'shared/lib/react-i18next/use-i18n-locale';
import { InteractionType } from 'shared/types/data-type';
import { eventIsFromInteractionType } from 'shared/utils/event-is-from-interaction-type';
import { focusOnEndOfEditableContent } from 'shared/utils/focus-on-end-of-element';
import { WordId } from 'shared/utils/nlp/get-ids-for-words';
import { useTheme } from 'styled-components';

import { useNewEntities } from './use-new-entities';

export type NewTaskRowProps = {
  date?: Timestamp;
  dateAnchored?: boolean;
  deadlineDeadline?: Timestamp;
  startTime?: number | null;
  duration?: number | null;
  goal?: ID;
  section?: ID;
  lifeArea?: ID;
  tags?: ID[];
  parentIds?: ID[] | null;
  parentChildIds?: ID[] | null;
  priority?: TaskPriorityOptions;
  schedules?: HabitSchedule[];
  newTaskInputRef?: React.Ref<HTMLDivElement>;
  hideMeta?: boolean;
};

export const NewTaskRow: React.FC<NewTaskRowProps> = React.memo(
  ({
    date,
    dateAnchored,
    deadlineDeadline,
    startTime,
    duration,
    goal,
    section,
    lifeArea,
    tags: initialTags,
    parentIds,
    parentChildIds,
    priority,
    schedules,
    newTaskInputRef,
    hideMeta,
  }) => {
    const inputRef = useForwardedRef(newTaskInputRef);
    const { t } = useTranslation();
    const locale = useI18nLocale();
    const taskRef = useRef<HTMLDivElement>(null);
    const [isSelected, setIsSelected] = useState(false);

    const [name, setName] = useState('');

    const theme = useTheme();
    const user = useUser();
    const timeFormat = getTimeFormat(user);
    const dateFormat = getDateFormat(user);
    const goals = useSortedGoals();
    const lifeAreas = useLocalizedLifeAreas();
    const tags = useSortedTags();
    const {
      goalsToCreate,
      tagsToCreate,
      onCreateGoal,
      onCreateTag,
      resetEntities,
    } = useNewEntities();

    const initialProperties = useMemo(
      () => ({
        endStrategy: date ? { deadline: date, completionCount: null } : null,
        deadlineDeadline: deadlineDeadline ?? null,
        timeslot:
          isNumber(startTime) || isNumber(duration)
            ? { startTime: startTime ?? null, duration: duration ?? null }
            : null,
        goalId: goal ?? null,
        sectionId: section ?? null,
        lifeAreaId: lifeArea ?? null,
        parentIds: parentIds ?? null,
        tagIds: initialTags ?? null,
        priority,
        schedules,
      }),
      [
        date,
        deadlineDeadline,
        duration,
        goal,
        initialTags,
        lifeArea,
        parentIds,
        priority,
        schedules,
        section,
        startTime,
      ],
    );
    const activeLifeAreas = useMemo(
      () => lifeAreas.filter(({ archivedAt }) => !archivedAt),
      [lifeAreas],
    );
    const activeAndCreateGoals = useMemo(
      () => [
        ...goals.filter(
          ({ completedAt, archivedAt }) => !completedAt && !archivedAt,
        ),
        ...goalsToCreate,
      ],
      [goals, goalsToCreate],
    );
    const activeAndCreateTags = useMemo(
      () => [...tags, ...tagsToCreate],
      [tags, tagsToCreate],
    );

    const { allowed: createHabitAllowed } = useCreateHabitAllowed();
    const { allowed: scheduleTargetAllowed } = usePremiumFeatureAllowed(
      LimitKeys.HabitScheduleTarget,
      {},
    );
    const { allowed: createGoalAllowed } = usePremiumFeatureAllowed(
      LimitKeys.CreateGoal,
      { totalUncompleted: activeAndCreateGoals.length },
    );

    const openPremiumDialog = useOpenPremiumDialog();
    const openCreateTaskColumn = useOpenCreateTask();
    const createTask = useCreateTaskWithEntities();
    const bulkCreateTasks = useCreateTasksWithEntities();
    const { submit: updateTask } = useUpdateTask();

    const openCreateMultipleTasksDialog = useCreateMultipleTasksDialog();

    const {
      task: newTask,
      updateTaskVariables: updateNewTask,
      subTasks,
      updateWordIds,
      updateIgnoredWords,
      reset: resetNewTaskState,
    } = useNewTaskState(initialProperties, {
      nlpString: name,
      updateNlpString: setName,
      goals: activeAndCreateGoals,
      tags: activeAndCreateTags,
      lifeAreas,
      createHabitAllowed,
      scheduleTargetAllowed,
      language: locale,
      timeFormat,
      dateFormat,
      dateAnchored,
    });

    const updateName = useCallback(
      (
        name: string,
        {
          wordIds,
          ignoredWordIds,
        }: { wordIds: WordId[]; ignoredWordIds: ID[] },
      ) => {
        setName(name);
        updateWordIds(wordIds);
        updateIgnoredWords(ignoredWordIds);
      },
      [updateIgnoredWords, updateWordIds],
    );

    const submitTask = async () => {
      if (!newTask.name) {
        return;
      }

      const createOptions = {
        task: newTask,
        subTasks,
        goalsToCreate,
        tagsToCreate,
      };

      setName('');
      resetEntities();
      resetNewTaskState();
      requestAnimationFrame(() => {
        if (inputRef.current) {
          focusOnEndOfEditableContent(inputRef.current);
        }
      });

      const task = await createTask(createOptions);

      const parentId = parentIds?.[parentIds.length - 1];

      if (parentId) {
        updateTask({
          id: parentId,
          childIds: [...(parentChildIds ?? []), task.id],
        });
      }
    };

    const onFocus = () => {
      theme.isMobile && openCreateTaskColumn(newTask);
      !theme.isMobile && setIsSelected(true);
    };

    const handleBulkCreate = async (tasks: CreateTask[]) => {
      const createOptions = {
        tasks,
        subTasks,
        goalsToCreate,
        tagsToCreate,
      };

      setName('');
      resetEntities();
      resetNewTaskState();
      requestAnimationFrame(() => {
        if (inputRef.current) {
          focusOnEndOfEditableContent(inputRef.current);
        }
      });

      await bulkCreateTasks(createOptions);
    };

    const handleBulkPaste = (tasksNames: string[]) => {
      openCreateMultipleTasksDialog(tasksNames, handleBulkCreate, {
        date,
        context: newTask,
        onCancel: () => setName(tasksNames.join(' ')),
      });
    };

    const updateNameAndSubmit = (
      name: string,
      { wordIds, ignoredWordIds }: { wordIds: WordId[]; ignoredWordIds: ID[] },
    ) => {
      if (!name) {
        return;
      }
      setName(name);
      updateWordIds(wordIds);
      updateIgnoredWords(ignoredWordIds);

      queueMicrotask(() => {
        submitTask();
      });
    };

    // reset the data when the task is not selected and initial props are changed
    useEffect(() => {
      if (!isSelected) {
        resetNewTaskState();
        resetEntities();
        setName('');
      }
    }, [
      isSelected,
      resetNewTaskState,
      initialProperties,
      updateName,
      resetEntities,
    ]);

    useClickOutside(taskRef, (e) => {
      if (isSelected && !eventIsFromInteractionType(e, InteractionType.Task)) {
        submitTask();
        setIsSelected(false);
      }
    });

    return (
      <NewTaskCard
        nameInputRef={inputRef}
        ref={taskRef}
        name={name}
        onChangeName={updateName}
        onSubmit={updateNameAndSubmit}
        type={newTask.type}
        createHabitAllowed={createHabitAllowed}
        scheduleTargetAllowed={scheduleTargetAllowed}
        onPremium={openPremiumDialog}
        createGoalAllowed={createGoalAllowed}
        onRequestSubmit={submitTask}
        placeholder={t(
          parentIds?.length
            ? 'forms.new-task.sub-task.title.placeholder'
            : 'forms.new-task.title.placeholder',
        )}
        description={newTask.description}
        goalId={newTask.goalId}
        lifeAreaId={newTask.lifeAreaId}
        tagIds={newTask.tagIds}
        goals={activeAndCreateGoals}
        lifeAreas={activeLifeAreas}
        tags={activeAndCreateTags}
        initialDeadline={date}
        deadline={newTask.endStrategy?.deadline ?? null}
        timeslot={newTask.timeslot ?? null}
        deadlineDeadline={newTask.deadlineDeadline ?? null}
        priority={newTask.priority}
        dateFormat={user?.settings?.dateFormat ?? initialDateFormat}
        weekStartsOn={getWeekStartsOn(user)}
        timeFormat={getTimeFormat(user)}
        schedules={newTask.schedules ?? null}
        reminderTime={newTask.reminderTime}
        onChange={updateNewTask}
        isSelected={isSelected}
        onClick={onFocus}
        onCreateGoal={onCreateGoal}
        onCreateTag={onCreateTag}
        onBulkPaste={handleBulkPaste}
        noMeta={hideMeta}
      />
    );
  },
);
